import { red } from '@mui/material/colors';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
// Remove dayjs import since it's not installed
import { ClassData } from "./useClasses";
import { SessionData } from "./useSession";
import { STATUS_OPTIONS, StatusType } from "../components/enum/StatusOptions";
import UploadBulkApplicationsModal from "components/Modals/UploadBulkApplicationsModal";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { useAuth } from "contexts/AuthContext";
import DownloadIcon from '@mui/icons-material/Download';

// Helper function to ensure dates are in dd/MM/yyyy format (as expected by backend)
const ensureValidDate = (date: string | Date | null | undefined): string => {
  if (!date) {
    // Return today's date as fallback
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }

  try {
    // Handle array format [year, month, day] from backend
    if (Array.isArray(date) && date.length >= 3) {
      const [year, month, day] = date;
      const dateObj = new Date(year, month - 1, day);
      if (!isNaN(dateObj.getTime())) {
        const dayStr = dateObj.getDate().toString().padStart(2, '0');
        const monthStr = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const yearStr = dateObj.getFullYear();
        return `${dayStr}/${monthStr}/${yearStr}`;
      }
    }

    // Handle string format
    if (typeof date === 'string') {
      // If it's already in dd/MM/yyyy format, return as is
      if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return date;
      }

      // If it's in YYYY-MM-DD format, convert to dd/MM/yyyy
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
      }
    }

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      // Return today's date as fallback
      const today = new Date();
      const day = today.getDate().toString().padStart(2, '0');
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const year = today.getFullYear();
      return `${day}/${month}/${year}`;
    }

    // Return in dd/MM/yyyy format (as expected by backend)
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    // Return today's date as fallback
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }
};

export interface ApplicationFormData {
  id?: number;
  // Step 1: Application & Enrollment Details
  applicationNo: string;
  rollNo: string;
  enrolmentNo: string;
  scholarNo: string;
  sssmid: string;
  penNo: string;
  apparId: string;
  studentCode?: string; // Student code field

  // Step 2: Student Personal Information
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  medium: string;
  religion: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  aadharNo: string;
  studentPhoto?: File | null; // For new uploads
  studentPhotoUrl?: string; // For existing photos (from backend)
  studentSignature?: File | null; // For new signature uploads
  studentSignatureUrl?: string; // For existing signatures (from backend)

  // Step 3: Contact & Address Information
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  gmail: string;

  // Step 4: Bank & Financial Information
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;

  // Step 5: Admission Details
  admissionDate: string;
  admissionClass: string;
  section: string;
  admissionType: string; // Admission Type field
  stream: string; // Stream field
  cwsnCode: string; // CWSN Code field
  sambalKramank: string; // Sambal Kramank field
  houseId?: number | string | null;
  house?: string | null;
  rte?: string; // RTE field

  // Step 6: Subjects - Backend expects List<String> (array of subject codes)
  subjects: string[];

  // Required fields
  schoolId: number;
  isRegistered: string; // "pending" | "approved" | "rejected"
  status?: boolean; // false for deleted applications (don't show in UI)
  sessionName?: string;
  sessionId?: number;
  classId?: number;
  isSibling?: boolean;

  // Internal use
  selectedClass?: ClassData | null;
  selectedSession?: SessionData | null;
  // Optional: when set, ApplicationFormTemplate uses student photo API instead of application photo API
  studentId?: number;
}

const API_URL = `${process.env.REACT_APP_API_URL}/application-forms`;

// Authentication helpers for photo fetching
const getAuthToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const getTenantSchema = () => localStorage.getItem('tenantSchema') || sessionStorage.getItem('tenantSchema');
const buildAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`
  };
  const tenantSchema = getTenantSchema();
  if (tenantSchema) {
    headers['X-Tenant-ID'] = tenantSchema;
  }
  return headers;
};

// Get application photo blob URL
const getApplicationPhotoBlob = async (fileName: string): Promise<string | null> => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/application-forms/photo/${fileName}`;
    const headers = buildAuthHeaders();
    const response = await fetch(url, { headers });

    // Handle 404 gracefully - application might not have a photo
    if (response.status === 404) {
      return null;
    }

    // Handle 401/403 gracefully - don't throw, return null
    if (response.status === 401 || response.status === 403) {
      return null;
    }

    if (!response.ok) {
      // For other errors, log but don't throw to prevent logout
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    // Don't throw errors - return null instead to prevent logout
    return null;
  }
};

const getAllApplicationForms = async (): Promise<ApplicationFormData[]> => {
  const response = await axios.get(API_URL);
  return response.data;
};

// Helper function to convert date to ISO format (yyyy-MM-dd) for API calls
export const formatDateToISO = (date: string | Date | null | undefined): string => {
  if (!date) return "";

  try {
    let dateObj: Date;

    // Detect patterns
    const ddMMyyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/;
    const isoPattern = /^\d{4}-\d{2}-\d{2}$/;

    if (typeof date === "string") {
      if (isoPattern.test(date)) {
        // Already ISO format (yyyy-MM-dd)
        return date;
      }

      if (ddMMyyyyPattern.test(date)) {
        // Parse as dd/MM/yyyy
        const [day, month, year] = date.split("/");
        dateObj = new Date(Number(year), Number(month) - 1, Number(day));
      } else {
        // Fallback
        dateObj = new Date(date);
      }
    } else {
      dateObj = new Date(date);
    }

    if (Number.isNaN(dateObj.getTime())) return "";

    // Convert to ISO yyyy-MM-dd
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const day = dateObj.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    return "";
  }
};


const getApplicationFromByMobileNoAndDOB = async (
  mobileNo: string,
  dob: string
): Promise<ApplicationFormData> => {
  // Convert date to ISO format (yyyy-MM-dd) for Spring Boot LocalDate parsing
  const isoDate = formatDateToISO(dob);
  if (!isoDate) {
    throw new Error('Invalid date format');
  }
  const response = await axios.get(`${API_URL}/byMobileDob?mobileNo=${mobileNo}&dateOfBirth=${isoDate}`);
  return response.data;
};

const getApplicationFormsBySchoolId = async (
  schoolId: number
): Promise<ApplicationFormData[]> => {
  const response = await axios.get(`${API_URL}`);
  const filteredData = response.data.filter((form: any) => form.schoolId === schoolId);
  return filteredData;
};

const getApplicationFormsBySessionIdAndClassId = async (
  sessionId: number,
  classId: number
): Promise<ApplicationFormData[]> => {
  const response = await axios.get(`${API_URL}/sessionId/classId`, {
    params: {
      sessionId,
      classId
    }
  });
  return response.data;
};

const getApplicationFormsBySessionIdAndClassName = async (
  sessionId: number,
  className: string
): Promise<ApplicationFormData[]> => {
  const safeClassName = encodeURIComponent(className);
  const response = await axios.get(
    `${API_URL}/sessionId/${sessionId}/className/${safeClassName}`
  );
  return response.data;
};

const createApplicationForm = async (
  data: ApplicationFormData
): Promise<ApplicationFormData> => {

  try {
    // Create FormData for multipart/form-data request
    const formData = new FormData();

    // Create the application form DTO (without the photo field)
    const applicationFormDto = {
      id: data.id,
      applicationNo: data.applicationNo,
      enrolmentNo: data.enrolmentNo,
      scholarNo: data.scholarNo,
      sssmid: data.sssmid,
      penNo: data.penNo,
      apparId: data.apparId,
      studentCode: data.studentCode,
      rollNo: data.rollNo,
      studentName: data.studentName,
      fatherName: data.fatherName,
      motherName: data.motherName,
      dateOfBirth: ensureValidDate(data.dateOfBirth),
      gender: data.gender,
      medium: data.medium,
      religion: data.religion,
      caste: data.caste,
      subCaste: data.subCaste,
      casteCertificate: data.casteCertificate,
      aadharNo: data.aadharNo,
      houseNo: data.houseNo,
      street: data.street,
      landmark: data.landmark,
      area: data.area,
      city: data.city,
      postOffice: data.postOffice,
      district: data.district,
      state: data.state,
      pinCode: data.pinCode,
      mobileNo: data.mobileNo,
      gmail: data.gmail,
      bankName: data.bankName,
      ifcCode: data.ifcCode,
      accountNo: data.accountNo,
      annualIncome: data.annualIncome,
      admissionDate: ensureValidDate(data.admissionDate),
      admissionClass: data.admissionClass,
      section: data.section,
      admissionType: data.admissionType,
      stream: data.stream || '', // Stream field with default empty string
      cwsnCode: data.cwsnCode,
      sambalKramank: data.sambalKramank,
      rte: data.rte,
      subjects: data.subjects,
      schoolId: data.schoolId,
      sessionId: data.sessionId,
      classId: data.classId,
      sessionName: data.sessionName,
      isSibling: data.isSibling,
      houseId: data.houseId,
    };

    // Add the application form DTO as a JSON string with correct Content-Type
    const jsonBlob = new Blob([JSON.stringify(applicationFormDto)], {
      type: 'application/json'
    });
    formData.append('applicationForm', jsonBlob);

    // Always add photo - send empty string if not provided (instead of null)
    if (data.studentPhoto) {
      formData.append('photo', data.studentPhoto);
    } else {
      formData.append('photo', '');
    }

    // Always add signature - send empty string if not provided (instead of null)
    if (data.studentSignature) {
      formData.append('signature', data.studentSignature);
    } else {
      formData.append('signature', '');
    }

    const response = await axios.post(`${API_URL}/create`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const updateApplicationForm = async (
  id: number,
  data: ApplicationFormData
): Promise<ApplicationFormData> => {

  try {
    // Create FormData for multipart/form-data request
    const formData = new FormData();

    // Create the application form DTO (without the photo field)
    const applicationFormDto = {
      id: data.id,
      applicationNo: data.applicationNo,
      enrolmentNo: data.enrolmentNo,
      scholarNo: data.scholarNo,
      sssmid: data.sssmid,
      penNo: data.penNo,
      apparId: data.apparId,
      studentCode: data.studentCode,
      rollNo: data.rollNo,
      studentName: data.studentName,
      fatherName: data.fatherName,
      motherName: data.motherName,
      dateOfBirth: ensureValidDate(data.dateOfBirth),
      gender: data.gender,
      medium: data.medium,
      religion: data.religion,
      caste: data.caste,
      subCaste: data.subCaste,
      casteCertificate: data.casteCertificate,
      aadharNo: data.aadharNo,
      houseNo: data.houseNo,
      street: data.street,
      landmark: data.landmark,
      area: data.area,
      city: data.city,
      postOffice: data.postOffice,
      district: data.district,
      state: data.state,
      pinCode: data.pinCode,
      mobileNo: data.mobileNo,
      gmail: data.gmail,
      bankName: data.bankName,
      ifcCode: data.ifcCode,
      accountNo: data.accountNo,
      annualIncome: data.annualIncome,
      admissionDate: ensureValidDate(data.admissionDate),
      admissionClass: data.admissionClass,
      section: data.section,
      admissionType: data.admissionType,
      stream: data.stream || '', // Stream field with default empty string
      cwsnCode: data.cwsnCode,
      sambalKramank: data.sambalKramank,
      rte: data.rte,
      subjects: data.subjects,
      schoolId: data.schoolId,
      sessionId: data.sessionId,
      classId: data.classId,
      sessionName: data.sessionName,
      isSibling: data.isSibling,
      houseId: data.houseId,
    };

    // Add the application form DTO as a JSON string with correct Content-Type
    const jsonBlob = new Blob([JSON.stringify(applicationFormDto)], {
      type: 'application/json'
    });
    formData.append('applicationForm', jsonBlob);

    // Add photo if provided
    if (data.studentPhoto) {
      formData.append('photo', data.studentPhoto);
    }

    // Add signature if provided
    if (data.studentSignature) {
      formData.append('signature', data.studentSignature);
    }

    const response = await axios.put(`${API_URL}/update/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const deleteApplicationForm = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

// Reject an application form (sets status to false without deleting)
const rejectApplicationForm = async (id: number): Promise<ApplicationFormData> => {
  const response = await axios.put(`${API_URL}/reject/${id}`);
  return response.data;
};

// Get application photo info by application ID
// Response includes: { fileName: string; filePath: string; studentSignature?: string }
const getApplicationPhotoInfo = async (id: number): Promise<{ fileName: string; filePath: string; studentSignature?: string } | null> => {
  try {
    const response = await axios.get(`${API_URL}/studentPhoto/${id}`);
    return response.data;
  } catch (error: any) {
    // Handle 404 gracefully - application might not have a photo, which is normal
    if (error?.response?.status === 404) {
      return null;
    }
    // Re-throw other errors (401/403 will be handled by axios interceptor)
    throw error;
  }
};

// Export the application photo info hook separately for easier importing
// Response includes signature path in studentSignature field
export const useApplicationPhotoInfo = (applicationId?: number) => {
  return useQuery<{ fileName: string; filePath: string; studentSignature?: string } | null, Error>(
    ["applicationPhotoInfo", applicationId],
    () => getApplicationPhotoInfo(applicationId!),
    {
      enabled: !!applicationId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on 404 (application has no photo) or 401/403 (auth errors)
        if (error?.response?.status === 404 || error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
    }
  );
};


// Hook to get application photo blob URL
export const useApplicationPhoto = (fileName?: string) => {
  const query = useQuery<string | null, Error>(
    ["applicationPhoto", fileName],
    () => getApplicationPhotoBlob(fileName!),
    {
      enabled: !!fileName,
      staleTime: 0, // Don't cache blob URLs - they need to be recreated each time
      cacheTime: 0, // Don't keep blob URLs in cache after unmount
      refetchOnWindowFocus: false,
      refetchOnMount: true, // Always refetch when component mounts to get fresh blob URL
      retry: (failureCount, error: any) => {
        // Don't retry on 404 (no photo) or 401/403 (auth errors)
        if (error?.response?.status === 404 || error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
    }
  );

  // Cleanup blob URL when component unmounts or fileName changes
  useEffect(() => {
    const blobUrl = query.data;
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [query.data]);

  return query;
};

const uploadBulkApplications = async (params: {
  file: File;
  schoolId: number;
  classId: number;
  sessionId: number;
}) => {
  const formData = new FormData();
  formData.append("file", params.file);
  formData.append("schoolId", params.schoolId.toString());
  formData.append("classId", params.classId.toString());
  formData.append("sessionId", params.sessionId.toString());

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/application-forms/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: 'blob' // Handle file response
      }
    );

    // Check if response is an Excel file (error case) or success message
    const contentType = response.headers['content-type'];

    if (contentType && (
      contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/excel')
    )) {
      // Response is an Excel file with errors
      return {
        success: false,
        message: "Some entries contain errors. Please download the error file to see the details.",
        errorFile: response.data
      };
    } else {
      // Response is a success message (text)
      // Convert blob to text
      const text = await response.data.text();
      return {
        success: true,
        message: text || "Bulk upload successful",
        errorFile: null
      };
    }
  } catch (error: any) {
    // Check if the error response contains an Excel file
    const contentType = error.response?.headers['content-type'];
    const isExcelError = contentType && (
      contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/excel')
    );

    if (error.response?.data instanceof Blob && isExcelError) {
      return {
        success: false,
        message: "Some entries contain errors. Please download the error file to see the details.",
        errorFile: error.response.data
      };
    }

    // For other errors, try to extract error message
    let errorMessage = "Bulk upload failed";
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        errorMessage = text || error.message || "Bulk upload failed";
      } catch {
        errorMessage = error.message || "Bulk upload failed";
      }
    } else {
      errorMessage = error.response?.data?.message || error.message || "Bulk upload failed";
    }

    throw new Error(errorMessage);
  }
};

export function useApplicationForm(tenantLoading: boolean = false, schoolId?: number, mobileNo?: string, dob?: string) {
  const queryClient = useQueryClient();

  // Clear all school-specific caches when schoolId changes
  useEffect(() => {
    if (schoolId) {
      // Remove all other school caches to prevent cross-school data
      queryClient.removeQueries({
        queryKey: ["applicationFormsBySchoolId"],
        predicate: (query) => {
          const querySchoolId = query.queryKey[1];
          return querySchoolId !== schoolId;
        }
      });
    }
  }, [schoolId, queryClient]);

  // Disabled getAll query - now using sessionId/classId specific query
  const applicationFormsQuery = useQuery<ApplicationFormData[], Error>({
    queryKey: ["applicationForms"],
    queryFn: getAllApplicationForms,
    enabled: false, // Disabled - using useApplicationFormsBySessionIdAndClassId instead
  });

  const applicationFormsByMobileNoAndDOBQuery = useQuery<ApplicationFormData, Error>({
    queryKey: ["applicationFormsByMobileNoAndDOB", mobileNo, dob],
    queryFn: () => getApplicationFromByMobileNoAndDOB(mobileNo!, dob!),
    enabled: !!mobileNo && !!dob && !tenantLoading,
    staleTime: 0,       // Always consider data stale so it refetches on every mount/invalidation
    cacheTime: 0,       // Don't keep result in cache after the component unmounts
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Fetch by schoolId if schoolId is provided, but disable if mobileNo and dob are provided
  // (to avoid unnecessary API calls when using mobile/DOB query instead)
  // DISABLED: Not needed in SchoolSelectionDashboard - only fetch when explicitly searching
  const applicationFormsBySchoolIdQuery = useQuery<
    ApplicationFormData[],
    Error
  >({
    queryKey: ["applicationFormsBySchoolId", schoolId],
    queryFn: () => {
      if (!schoolId) return Promise.resolve([]);
      return getApplicationFormsBySchoolId(schoolId);
    },
    enabled: false, // Disabled - don't fetch all application forms automatically
    staleTime: 0, // Always fetch fresh data when schoolId changes
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Hook to fetch by sessionId and classId
  const useApplicationFormsBySessionIdAndClassId = (sessionId?: number, classId?: number) =>
    useQuery<ApplicationFormData[], Error>({
      queryKey: ["applicationFormsBySessionIdAndClassId", sessionId, classId],
      queryFn: () => {
        if (!sessionId || !classId || classId <= 0) return Promise.resolve([]);
        return getApplicationFormsBySessionIdAndClassId(sessionId, classId);
      },
      enabled: !!sessionId && !!classId && classId > 0 && !tenantLoading,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const useApplicationFormsBySessionIdAndClassName = (sessionId?: number, className?: string) =>
    useQuery<ApplicationFormData[], Error>({
      queryKey: ["applicationFormsBySessionIdAndClassName", sessionId, className],
      queryFn: () => {
        if (!sessionId || !className) return Promise.resolve([]);
        return getApplicationFormsBySessionIdAndClassName(sessionId, className);
      },
      enabled: !!sessionId && !!className && !tenantLoading,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const createApplicationFormMutation = useMutation({
    mutationFn: createApplicationForm,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["applicationForms"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["applicationFormsBySchoolId", schoolId] });
      }
      // Invalidate sessionId/classId query
      if (data?.sessionId && data?.classId) {
        queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySessionIdAndClassId", data.sessionId, data.classId]
        });
      }
    },
  });

  const updateApplicationFormMutation = useMutation({
    mutationFn: (args: { id: number; data: ApplicationFormData }) =>
      updateApplicationForm(args.id, args.data),
    onSuccess: (updatedForm) => {
      // Update the cache immediately
      queryClient.setQueryData(["applicationForms"], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((form: any) =>
          form.id === updatedForm.id ? updatedForm : form
        );
      });

      if (schoolId) {
        queryClient.setQueryData(
          ["applicationFormsBySchoolId", schoolId],
          (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((form: any) =>
              form.id === updatedForm.id ? updatedForm : form
            );
          }
        );
      }

      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["applicationForms"] });
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySchoolId", schoolId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      queryClient.invalidateQueries({ queryKey: ["tclist"] }); // Invalidate TC list to show updated students

      // Invalidate sessionId/classId query whenever application form is updated (not just when approving)
      if (updatedForm?.sessionId && updatedForm?.classId) {
        queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySessionIdAndClassId", updatedForm.sessionId, updatedForm.classId]
        });
      }
      // Invalidate the byMobileDob query so the lookup page reflects the latest data
      queryClient.invalidateQueries({ queryKey: ["applicationFormsByMobileNoAndDOB"] });
    },
  });

  const deleteApplicationFormMutation = useMutation({
    mutationFn: deleteApplicationForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applicationForms"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["applicationFormsBySchoolId", schoolId] });
      }
    },
  });

  const rejectApplicationFormMutation = useMutation({
    mutationFn: rejectApplicationForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applicationForms"] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ["applicationFormsBySchoolId", schoolId] });
      }
    },
  });

  const uploadBulkApplicationsMutation = useMutation({
    mutationFn: uploadBulkApplications,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["applicationForms"] });
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySchoolId", schoolId],
        });
      }
      // Invalidate sessionId/classId query
      if (variables?.sessionId && variables?.classId) {
        queryClient.invalidateQueries({
          queryKey: ["applicationFormsBySessionIdAndClassId", variables.sessionId, variables.classId]
        });
      }
    },
  });


  return {
    applicationFormsQuery,
    createApplicationFormMutation,
    updateApplicationFormMutation,
    deleteApplicationFormMutation,
    rejectApplicationFormMutation,
    applicationFormsBySchoolIdQuery,
    uploadBulkApplicationsMutation,
    applicationFormsByMobileNoAndDOBQuery,
    useApplicationFormsBySessionIdAndClassId,
    useApplicationFormsBySessionIdAndClassName,
  };
}
